// Request queue with rate limiting protection
class RequestQueue {
  constructor(maxConcurrent = 6, delayBetweenRequests = 50) {
    this.queue = [];
    this.running = 0;
    this.maxConcurrent = maxConcurrent; // Allow 6 concurrent requests
    this.delayBetweenRequests = delayBetweenRequests; // 50ms delay between requests
    this.retryAttempts = 2;
    this.retryDelay = 500; // Initial retry delay of 500ms
  }

  async add(requestFn, retries = this.retryAttempts) {
    return new Promise((resolve, reject) => {
      this.queue.push({ requestFn, resolve, reject, retries });
      this.process();
    });
  }

  async process() {
    if (this.running >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    this.running++;
    const { requestFn, resolve, reject, retries } = this.queue.shift();

    try {
      const result = await requestFn();
      resolve(result);
    } catch (error) {
      // Check if it's a 429 error
      if (error.message && error.message.includes('429') && retries > 0) {
        console.warn(`Rate limit hit, retrying... (${retries} attempts left)`);

        // Calculate exponential backoff delay
        const backoffDelay = this.retryDelay * Math.pow(2, this.retryAttempts - retries);

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, backoffDelay));

        // Add back to queue with reduced retry count
        this.queue.unshift({ requestFn, resolve, reject, retries: retries - 1 });
        this.running--;
        this.process();
        return;
      }

      reject(error);
    } finally {
      if (retries === this.retryAttempts || retries === 0) {
        this.running--;
      }

      // Add delay before processing next request
      if (this.queue.length > 0) {
        setTimeout(() => this.process(), this.delayBetweenRequests);
      }
    }
  }

  // Clear all pending requests
  clear() {
    this.queue = [];
  }

  // Get queue status
  getStatus() {
    return {
      queued: this.queue.length,
      running: this.running,
      maxConcurrent: this.maxConcurrent
    };
  }
}

export const requestQueue = new RequestQueue();
export default RequestQueue;
